import React, { useCallback, useEffect, useRef } from "react";
import { Figure as FigureBase } from "../Figure";
import { CategoryChart, prefixValue, suffixValue } from "../processors";
import { FigureKpiProviders } from "../categoryChartQueryFns";
import { Paragraph } from "../Paragraph";
import { useCategoryContext } from "../Context";
import { getHighlightColors } from "../../themes";
import { CategoryTheme, PROVIDER_TITLE_CLASS } from "../misc";
import ReactResizeDetector from "react-resize-detector";
import _ from "lodash";

import styles from "./CategoryChart.module.scss";

interface FigureProvidersProps extends FigureKpiProviders {
    categoryChart: CategoryChart;
}

export const FigureProviders: React.FC<FigureProvidersProps> = ({
    title,
    footer,
    categoryChart,
    figures: figuresSet,
    waterProviders
}) => {
    // const figuresSet = _figuresSet.map((d, index) => {
    //     return d.map(d => {
    //         return index === 0
    //             ? {
    //                   ...d,
    //                   value: "Not applicable"
    //               }
    //             : d;
    //     });
    // });

    const { category } = useCategoryContext();
    const divRef = useRef<HTMLDivElement>();
    const updateFigureHeights = useCallback(() => {
        if (divRef?.current) {
            const divWidth = divRef?.current?.clientWidth;

            const figureLength = _.max(figuresSet?.map(d => d.length ?? 0));

            // if only one figure from each provider is given
            if (figureLength === 1) {
                const content = divRef?.current?.querySelectorAll(".std-figure .content");

                const nonLabelMaxHeight =
                    _.max(
                        _.flattenDeep(
                            Array.from(content).map(d => {
                                return Array.from(d.children)
                                    .filter(d => d.tagName?.toLowerCase() !== "label")
                                    .map(d => {
                                        return d?.getBoundingClientRect()?.height ?? 1;
                                    });
                            })
                        )
                    ) ?? 0;

                // if (category?.adminId === "water-supply-asset-condition-water-losses") {
                //     console.log(
                //         content,
                //         nonLabelMaxHeight,
                //         Array.from(content).map(d => {
                //             return Array.from(d.children).filter(d => d.tagName?.toLowerCase() !== "label");
                //         }),
                //         category
                //     );
                // }
                // when only labels are displayed, make sure equal heights from both sides
                if (nonLabelMaxHeight === 0) {
                    const maxHeight = _.max(
                        _.flattenDeep(
                            Array.from(content).map(d => {
                                return Array.from(d.children).map(d => {
                                    return d.clientHeight ?? 0;
                                });
                            })
                        )
                    );

                    if (divWidth < 716) {
                        Array.from(content).forEach(d => {
                            (d as HTMLDivElement).style.height = `unset`;
                            (d as HTMLDivElement).style.display = `block`;
                            (d as HTMLDivElement).style.alignItems = `center`;
                            (d as HTMLDivElement).style.justifyContent = `center`;
                        });
                    } else {
                        Array.from(content).forEach(d => {
                            (d as HTMLDivElement).style.height = `${maxHeight}px`;
                            (d as HTMLDivElement).style.display = `flex`;
                            (d as HTMLDivElement).style.alignItems = `center`;
                            (d as HTMLDivElement).style.justifyContent = `center`;
                        });
                    }
                }
                // otherwise not doing anything
                else {
                    Array.from(content).forEach(d => {
                        (d as HTMLDivElement).style.height = `unset`;
                        (d as HTMLDivElement).style.display = `block`;
                        (d as HTMLDivElement).style.alignItems = `unset`;
                        (d as HTMLDivElement).style.justifyContent = `unset`;
                    });
                }
            }
        }
    }, [figuresSet]);

    useEffect(() => {
        updateFigureHeights();
    }, [updateFigureHeights]);

    const onResize = useCallback(() => {
        updateFigureHeights();
    }, [updateFigureHeights]);

    if (!category) return null;

    const theme = category.theme as CategoryTheme;

    const className = `col-md-${12 / (figuresSet?.filter(d => d != null)?.length ?? 1)} col-12`;

    // getting colors for water providers
    const colors = getHighlightColors(theme, figuresSet?.length);
    return (
        <>
            {title != null && <Paragraph title={""} status={true} nid={0} text={title} className="col-12" />}
            <div className="row" ref={divRef}>
                {figuresSet?.map((figures, index) => {
                    return (
                        <React.Fragment key={index}>
                            {figures != null && waterProviders[index] != null && (
                                <div
                                    className={`${className} d-flex`}
                                    style={{
                                        flexWrap: "wrap"
                                    }}
                                >
                                    <div className={`${styles.providerTitle} ${PROVIDER_TITLE_CLASS} col-12 p-0`}>
                                        {waterProviders[index].name}
                                    </div>

                                    {figures?.map((figure, figureIndex) => {
                                        return (
                                            <React.Fragment key={figureIndex}>
                                                {figure != null && (
                                                    <FigureBase
                                                        unit={figure?.unit}
                                                        categoryChart={categoryChart}
                                                        className="figure-container col p-0"
                                                        figureStyle={{
                                                            color: colors[index]
                                                        }}
                                                        decimals={figure.decimals}
                                                        header={
                                                            !figure.header ? null : (
                                                                <Paragraph
                                                                    title={""}
                                                                    status={true}
                                                                    nid={0}
                                                                    text={figure.header}
                                                                />
                                                            )
                                                        }
                                                        footer={
                                                            !figure.footer ? null : (
                                                                <Paragraph
                                                                    title={""}
                                                                    status={true}
                                                                    nid={0}
                                                                    text={figure.footer}
                                                                />
                                                            )
                                                        }
                                                        value={figure.value}
                                                        prefix={prefixValue(figure.value, figure.unit)}
                                                        suffix={suffixValue(figure.value, figure.unit)}
                                                        unitInSuffix={figure?.unit}
                                                    />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}

                <ReactResizeDetector handleWidth={true} onResize={onResize}></ReactResizeDetector>
            </div>
            {footer != null && <Paragraph title={""} status={true} nid={0} text={footer} className="col-12" />}
        </>
    );
};
